/**
 * lbmgb-consent-banner.js — region-aware consent banner for storefronts on
 * Shopify (Basic / Shopify / Advanced). Per ADR-4.21.
 *
 * Why we own a banner instead of using Shopify's: the basic Customer Privacy
 * API on non-Plus plans collapses consent to a single boolean
 * (`userCanBeTracked()`); per-region defaults aren't configurable in admin;
 * `openCustomerPrivacyDialog()` and `marketingAllowed()` /
 * `analyticsProcessingAllowed()` are Plus-only. We need granular Analytics +
 * Marketing toggles to drive Google Consent Mode v2, plus EU-vs-US default
 * differentiation, plus a banner-reopen entry point for the CCPA "Do Not
 * Sell" link. So we render the UI ourselves and call
 * `Shopify.customerPrivacy.setTrackingConsent(...)` to keep Shopify's own
 * cookie bookkeeping in sync.
 *
 * Region zones (set by lbmgb-tracking.liquid from localization.country.iso_code):
 *   EU_OPT_IN     — banner blocks until visitor chooses; default deny.
 *   US_OPT_OUT    — silent default-grant. The footer "Do Not Sell or Share My
 *                   Personal Information" link (rendered by lbmgb-opt-out-link
 *                   for US visitors) is the legally-required disclosure under
 *                   CCPA/CPRA + similar state laws (CO/VA/CT/UT). None of those
 *                   laws require a visible toast/banner alerting users to the
 *                   opt-out — they only require a clear and conspicuous link,
 *                   which we render. We initially shipped a toast for US
 *                   visitors but it surfaced unnecessarily in non-CCPA states
 *                   like NC/TX/etc. State-level geo isn't reliably available
 *                   storefront-side without adding an external GeoIP call, and
 *                   the toast was never legally required, so dropped.
 *   ROW           — silent default-grant.
 *
 * Storage: localStorage['lbmgb_consent_v1'] = {
 *   analytics: bool, marketing: bool, ts: epoch_ms, v: 1
 * }
 * Stale after 13 months (EU guidance — repeat consent ask annually-ish).
 *
 * Legal posture (per EDPB Guidelines 03/2022 + CNIL enforcement):
 *   - Accept All and Decline All buttons are equal-weight (same size, same
 *     contrast, same affordance). Styling lives in lbmgb-consent-banner.css
 *     and must not introduce hierarchy between them.
 *   - No outside-click or scroll dismissal in EU. User must click a button.
 *   - Toggles inside "Manage preferences" default OFF in EU.
 *   - No "Recommended" labels, no pre-ticked boxes, no guilt copy.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'lbmgb_consent_v1';
  var STORAGE_VERSION = 1;
  var TTL_MS = 13 * 30 * 24 * 60 * 60 * 1000; // ~13 months

  function debug() {
    if (window.LBMGB && window.LBMGB.config && window.LBMGB.config.debug) {
      var args = ['[LBMGB consent]'].concat([].slice.call(arguments));
      console.log.apply(console, args);
    }
  }

  function readStored() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var v = JSON.parse(raw);
      if (!v || v.v !== STORAGE_VERSION || typeof v.analytics !== 'boolean'
          || typeof v.marketing !== 'boolean' || typeof v.ts !== 'number') {
        return null;
      }
      if (Date.now() - v.ts > TTL_MS) return null;
      return v;
    } catch (e) { return null; }
  }

  function writeStored(analytics, marketing) {
    var v = { analytics: !!analytics, marketing: !!marketing,
              ts: Date.now(), v: STORAGE_VERSION };
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(v)); } catch (e) {}
    return v;
  }

  // Tracking-cookie prefixes wiped on full opt-out. Sephora settlement (2022)
  // cited cookie-persistence-despite-opt-out as the violation pattern, so we
  // proactively delete known ad/analytics cookies when the user revokes both
  // categories. Essential Shopify cookies (_shopify_*, cart_currency, _secure_session_id)
  // are intentionally excluded — wiping those would break the cart.
  //
  //   __kla_id       Klaviyo profile cookie
  //   _fbp / _fbc    Meta browser pixel + click ID
  //   _uetmsclkid / _uetsid / _uetvid   Microsoft UET
  //   _ga / _gid / _gcl_*               Google Analytics + Ads click ID
  //   _clck / _clsk                     Microsoft Clarity
  var TRACKING_COOKIE_PREFIXES = [
    '__kla_id', '_fbp', '_fbc',
    '_uetmsclkid', '_uetsid', '_uetvid',
    '_ga', '_gid', '_gcl_',
    '_clck', '_clsk'
  ];

  function wipeTrackingCookies() {
    var expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
    var host = location.hostname;
    var parentDomain = '.' + host.split('.').slice(-2).join('.');
    var configDomain = (window.LBMGB && window.LBMGB.config && window.LBMGB.config.cookieDomain) || '';
    // Domain coverage: bare host (path-scoped cookie), explicit current host,
    // parent eTLD+1 (covers cdn/checkout subdomains), and any explicit
    // cookieDomain set by the merchant config.
    var domains = [null, host, parentDomain];
    if (configDomain && domains.indexOf(configDomain) === -1) domains.push(configDomain);

    document.cookie.split(';').forEach(function (raw) {
      var name = raw.split('=')[0].trim();
      if (!name) return;
      var matches = TRACKING_COOKIE_PREFIXES.some(function (p) { return name.indexOf(p) === 0; });
      if (!matches) return;
      domains.forEach(function (d) {
        var base = name + '=; expires=' + expires + '; path=/;';
        if (d) base += ' domain=' + d + ';';
        document.cookie = base;
      });
    });
    debug('tracking cookies wiped on revoke');
  }

  // Push consent to GTM Consent Mode v2 + LBMGB.track buffer + Shopify CP.
  function applyConsent(analytics, marketing) {
    window.dataLayer = window.dataLayer || [];
    function gtag() { window.dataLayer.push(arguments); }
    gtag('consent', 'update', {
      ad_storage:         marketing ? 'granted' : 'denied',
      ad_user_data:       marketing ? 'granted' : 'denied',
      ad_personalization: marketing ? 'granted' : 'denied',
      analytics_storage:  analytics ? 'granted' : 'denied'
    });
    if (window.LBMGB && window.LBMGB.consent) {
      if (analytics) window.LBMGB.consent.grant();
      else           window.LBMGB.consent.revoke();
    }
    // Mirror to Shopify's customer-privacy state. setTrackingConsent is
    // documented across all Shopify plans (Basic/Shopify/Advanced/Plus).
    // It writes Shopify's own consent cookies + dispatches
    // visitorConsentCollected so any other listener (Shopify-native pixels,
    // app-side trackers) sees the same state.
    if (window.Shopify && window.Shopify.customerPrivacy
        && typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function') {
      try {
        window.Shopify.customerPrivacy.setTrackingConsent({
          analytics: !!analytics,
          marketing: !!marketing,
          preferences: true,        // functionality cookies stay on
          sale_of_data: !!marketing // CCPA: opt-out of sale = marketing off
        }, function () { /* no-op */ });
      } catch (e) { debug('setTrackingConsent threw', e); }
    }
    // Sephora-pattern guard: when both categories are denied, proactively
    // wipe known tracking cookies. Shopify CP + gtag('consent','update') stop
    // *new* writes, but pre-existing cookies persist until their own expiry —
    // which is the exact pattern the California AG enforced against.
    if (!analytics && !marketing) {
      try { wipeTrackingCookies(); } catch (e) { debug('wipeTrackingCookies threw', e); }
    }
    debug('consent applied', { analytics: !!analytics, marketing: !!marketing });
  }

  // ---------------------------------------------------------------------------
  // DOM rendering
  // ---------------------------------------------------------------------------

  // Defaults reasonable for the current portfolio; merchant-overridable via
  // theme settings in a later pass.
  var COPY = {
    banner_title:       'Cookies on this site',
    banner_body:        'We use cookies and similar technologies to measure how the site is used and to personalize advertising. You can accept everything, decline everything, or pick what you want to allow.',
    accept_all:         'Accept all',
    decline_all:        'Decline all',
    manage:             'Manage preferences',
    modal_title:        'Privacy preferences',
    modal_body:         'Choose which categories of cookies you want to allow. Strictly-necessary cookies are always on so the site works.',
    cat_analytics:      'Analytics',
    cat_analytics_desc: 'Helps us understand how the site is used so we can improve it.',
    cat_marketing:      'Marketing',
    cat_marketing_desc: 'Lets us measure ad performance and show you more relevant advertising.',
    save_prefs:         'Save preferences'
  };

  function el(tag, attrs, children) {
    var n = document.createElement(tag);
    if (attrs) for (var k in attrs) {
      if (k === 'class') n.className = attrs[k];
      else if (k === 'text') n.textContent = attrs[k];
      else if (k.indexOf('on') === 0 && typeof attrs[k] === 'function') {
        n.addEventListener(k.slice(2), attrs[k]);
      } else n.setAttribute(k, attrs[k]);
    }
    (children || []).forEach(function (c) {
      if (c == null) return;
      if (typeof c === 'string') n.appendChild(document.createTextNode(c));
      else n.appendChild(c);
    });
    return n;
  }

  var bannerEl = null;
  var modalEl = null;
  var lastFocusBeforeOpen = null;

  function trapFocus(container, e) {
    if (e.key !== 'Tab') return;
    var focusable = container.querySelectorAll(
      'button, [href], input, [tabindex]:not([tabindex="-1"])');
    if (!focusable.length) return;
    var first = focusable[0], last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  function renderBanner() {
    if (bannerEl) return bannerEl;
    var titleId = 'lbmgb-banner-title';
    var bodyId = 'lbmgb-banner-body';
    bannerEl = el('div', {
      'class': 'lbmgb-consent-banner',
      'role': 'dialog',
      'aria-modal': 'false',
      'aria-labelledby': titleId,
      'aria-describedby': bodyId
    }, [
      el('div', { 'class': 'lbmgb-consent-banner__inner' }, [
        el('div', { 'class': 'lbmgb-consent-banner__copy' }, [
          el('h2', { id: titleId, 'class': 'lbmgb-consent-banner__title',
                     text: COPY.banner_title }),
          el('p',  { id: bodyId,  'class': 'lbmgb-consent-banner__body',
                     text: COPY.banner_body })
        ]),
        el('div', { 'class': 'lbmgb-consent-banner__actions' }, [
          el('button', {
            type: 'button',
            'class': 'lbmgb-consent-btn lbmgb-consent-btn--accept',
            text: COPY.accept_all,
            onclick: function () { onAcceptAll(); }
          }),
          el('button', {
            type: 'button',
            'class': 'lbmgb-consent-btn lbmgb-consent-btn--decline',
            text: COPY.decline_all,
            onclick: function () { onDeclineAll(); }
          }),
          el('button', {
            type: 'button',
            'class': 'lbmgb-consent-btn lbmgb-consent-btn--manage',
            text: COPY.manage,
            onclick: function () { openModal(); }
          })
        ])
      ])
    ]);
    document.body.appendChild(bannerEl);
    return bannerEl;
  }

  function showBanner() {
    renderBanner();
    bannerEl.classList.add('is-visible');
    // Move focus into the banner after a tick so screen readers announce it.
    var firstBtn = bannerEl.querySelector('button');
    setTimeout(function () { if (firstBtn) firstBtn.focus(); }, 50);
  }

  function hideBanner() {
    if (bannerEl) bannerEl.classList.remove('is-visible');
  }

  function renderModal(initial) {
    if (modalEl) return modalEl;
    var titleId = 'lbmgb-modal-title';
    var bodyId = 'lbmgb-modal-body';
    var analyticsCheckbox = el('input', {
      type: 'checkbox',
      id: 'lbmgb-cat-analytics',
      'class': 'lbmgb-consent-toggle__input'
    });
    var marketingCheckbox = el('input', {
      type: 'checkbox',
      id: 'lbmgb-cat-marketing',
      'class': 'lbmgb-consent-toggle__input'
    });
    analyticsCheckbox.checked = !!initial.analytics;
    marketingCheckbox.checked = !!initial.marketing;

    function row(checkbox, label, desc) {
      return el('div', { 'class': 'lbmgb-consent-row' }, [
        el('div', { 'class': 'lbmgb-consent-row__main' }, [
          el('label', { 'class': 'lbmgb-consent-toggle',
                        'for': checkbox.id }, [
            checkbox,
            el('span', { 'class': 'lbmgb-consent-toggle__track' }, [
              el('span', { 'class': 'lbmgb-consent-toggle__thumb' })
            ]),
            el('span', { 'class': 'lbmgb-consent-row__label', text: label })
          ]),
          el('p', { 'class': 'lbmgb-consent-row__desc', text: desc })
        ])
      ]);
    }

    modalEl = el('div', {
      'class': 'lbmgb-consent-modal',
      'role': 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': titleId,
      'aria-describedby': bodyId
    }, [
      el('div', { 'class': 'lbmgb-consent-modal__backdrop' }),
      el('div', { 'class': 'lbmgb-consent-modal__panel' }, [
        el('h2', { id: titleId, 'class': 'lbmgb-consent-modal__title',
                   text: COPY.modal_title }),
        el('p',  { id: bodyId,  'class': 'lbmgb-consent-modal__body',
                   text: COPY.modal_body }),
        row(analyticsCheckbox, COPY.cat_analytics, COPY.cat_analytics_desc),
        row(marketingCheckbox, COPY.cat_marketing, COPY.cat_marketing_desc),
        el('div', { 'class': 'lbmgb-consent-modal__actions' }, [
          el('button', {
            type: 'button',
            'class': 'lbmgb-consent-btn lbmgb-consent-btn--save',
            text: COPY.save_prefs,
            onclick: function () {
              onSavePrefs(analyticsCheckbox.checked, marketingCheckbox.checked);
            }
          })
        ])
      ])
    ]);
    modalEl.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeModal();
      else trapFocus(modalEl, e);
    });
    document.body.appendChild(modalEl);
    return modalEl;
  }

  function openModal() {
    var initial = readStored() || { analytics: false, marketing: false };
    renderModal(initial);
    modalEl.classList.add('is-visible');
    lastFocusBeforeOpen = document.activeElement;
    var firstInput = modalEl.querySelector('input');
    setTimeout(function () { if (firstInput) firstInput.focus(); }, 50);
  }

  function closeModal() {
    if (modalEl) {
      modalEl.classList.remove('is-visible');
      // Detach so a future open() re-renders with fresh `initial`.
      if (modalEl.parentNode) modalEl.parentNode.removeChild(modalEl);
      modalEl = null;
    }
    if (lastFocusBeforeOpen && lastFocusBeforeOpen.focus) {
      lastFocusBeforeOpen.focus();
    }
  }

  // ---------------------------------------------------------------------------
  // Button handlers
  // ---------------------------------------------------------------------------

  function onAcceptAll() {
    writeStored(true, true);
    applyConsent(true, true);
    hideBanner();
    closeModal();
  }

  function onDeclineAll() {
    writeStored(false, false);
    applyConsent(false, false);
    hideBanner();
    closeModal();
  }

  function onSavePrefs(analytics, marketing) {
    writeStored(analytics, marketing);
    applyConsent(analytics, marketing);
    hideBanner();
    closeModal();
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  // One-time cleanup of the stale lbmgb_klaro_consent cookie left over from
  // the Klaro CMP era (pre-ADR-4.21). Nothing reads it now, but it's
  // confusing in DevTools when debugging consent state.
  function deleteLegacyKlaroCookie() {
    if (!document.cookie.split('; ').some(function (c) {
      return c.indexOf('lbmgb_klaro_consent=') === 0;
    })) return;
    var domain = (window.LBMGB && window.LBMGB.config && window.LBMGB.config.cookieDomain) || '';
    // Try a few flavors since the original Klaro write didn't always pin domain.
    var expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = 'lbmgb_klaro_consent=; expires=' + expires + '; path=/';
    if (domain) {
      document.cookie = 'lbmgb_klaro_consent=; expires=' + expires + '; path=/; domain=' + domain;
    }
    debug('legacy lbmgb_klaro_consent cookie cleared');
  }

  // Global Privacy Control detection per W3C draft + CCPA §1798.135(b)(1).
  // When navigator.globalPrivacyControl === true we treat it as a verifiable
  // consumer opt-out request — equivalent to clicking the footer
  // "Do Not Sell or Share My Personal Information" link.
  //
  // Strict reading per California AG's Sephora settlement (2022): GPC must
  // suppress the *marketing* category (third-party "sharing") at minimum.
  // We additionally suppress *analytics* because our analytics path forwards
  // to ad platforms via sGTM and the lines blur. Conservative is safe.
  function detectsGpc() {
    try {
      // navigator.globalPrivacyControl is a boolean true when sent.
      return !!(window.navigator && window.navigator.globalPrivacyControl === true);
    } catch (e) {
      return false;
    }
  }

  function boot() {
    deleteLegacyKlaroCookie();
    var cfg = (window.LBMGB && window.LBMGB.config) || {};
    var zone = cfg.regionZone || 'ROW';
    var stored = readStored();
    var gpc = detectsGpc();

    if (gpc) {
      // GPC overrides any stored grant — the browser signal is the live
      // request. Treat as marketing+analytics decline. Record the
      // preference so we don't keep re-applying on every navigation, but
      // mark it so a user-initiated Accept later can override.
      //
      // The synchronous Liquid IIFE in lbmgb-tracking.liquid runs first and
      // already wrote the same opt-out + denied gtag update before GTM
      // loaded. This branch is the deferred-JS defensive backup — same
      // outcome, plus the `gpc: true` audit flag tagged onto the stored
      // record (preserved when the IIFE already wrote one; written fresh
      // here if the visitor is on a path that bypassed the IIFE, e.g. JS
      // ordering edge case or settings.lbmgb_consent_mode_enabled=false on
      // an older page render).
      debug('GPC signal detected — applying opt-out');
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
          analytics: false, marketing: false, ts: Date.now(),
          v: STORAGE_VERSION, gpc: true
        }));
      } catch (e) {}
      applyConsent(false, false);
      return;
    }

    // No live GPC signal, but stored record came from a prior GPC visit.
    // Keep the opt-out in place — do not auto-revert just because the
    // browser signal is currently off. A user-initiated Accept via the
    // footer "Do Not Sell" → Manage Preferences flow can clear it.
    if (stored && stored.gpc === true) {
      debug('stored GPC opt-out present — keeping consent denied');
      applyConsent(false, false);
      return;
    }

    if (stored) {
      // Returning visitor with a recorded choice. Apply silently.
      applyConsent(stored.analytics, stored.marketing);
      return;
    }

    if (zone === 'EU_OPT_IN') {
      // No stored choice. Default-deny is already in effect from the Liquid
      // snippet's gtag('consent', 'default', ...) call. Show banner and
      // wait for user action — no consent-by-browsing in EU.
      showBanner();
      return;
    }

    // US_OPT_OUT and ROW: silent default-grant. The footer "Do Not Sell"
    // link satisfies CCPA/CPRA-class state laws without a toast.
    writeStored(true, true);
    applyConsent(true, true);
  }

  // Public API for the footer "Do Not Sell" link and any future re-prompts.
  window.LBMGB = window.LBMGB || {};
  window.LBMGB.banner = {
    show:    function () { openModal(); },
    revoke:  function () { onDeclineAll(); },
    accept:  function () { onAcceptAll(); }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
